import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { searchCurrentCourseProvisions } from '../../../store/courseService';
import { loadAllExternalCourseProviderDetails } from '../../../store/directusService';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../utils/formValidation/commonConstants';
import { stableSort } from '../../../utils/sortFunctions';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';

import CurrentProvisionTableRow from './CurrentProvisionTableRow';

import app from '../../../app.module.css';

const headCells = [
    { id: 'id', numeric: false, label: 'ID' },
    { id: 'provisionName', numeric: false, label: 'Provision Name' },
    { id: 'providerName', numeric: false, label: 'Provider Name' },
    { id: 'status', numeric: false, label: 'Status' }
];

const CurrentProvisionManagement = ({ isDisabled, returnState }) => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [openId, setOpenId] = useState('');
    const [rowMetaData, setRowMetaData] = useState({
        order: 'asc',
        orderBy: 'name',
        page: 0,
        rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
    });
    const [activeCourseProvisionsSearchData, setActiveCourseProvisionsSearchData] = useState({});

    // STORE STATE
    const externalCourseProviderDetails = useSelector(
        (state) => state.entities.directusService.externalCourseProviderDetails
    );

    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const currentCourseProvisions = useSelector(
        (state) => state.entities.courseService.currentCourseProvisions
    );

    const currentCourseProvisionsMetaData = useSelector(
        (state) => state.entities.courseService.currentCourseProvisionsMetaData
    );
    const loadingCurrentProvisions = useSelector(
        (state) => state.entities.courseService.loadingCurrentProvisions
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        dispatch(loadAllExternalCourseProviderDetails());
    }, []);

    useEffect(() => {
        if (!('id' in currentParticipant)) return;
        const searchData = {
            active: true,
            contractIds: [currentParticipant.contractId],
            participantIds: [currentParticipant.id]
        };
        dispatch(searchCurrentCourseProvisions(searchData));
        setActiveCourseProvisionsSearchData(searchData);
    }, [currentParticipant]);

    useEffect(() => {
        if (currentCourseProvisions?.length < 1 || externalCourseProviderDetails?.length < 1)
            return;
        const formattedCourseProvisions = currentCourseProvisions?.map((el) => ({
            ...el,
            provisionName: el.title,
            providerName:
                externalCourseProviderDetails?.find((entry) => entry.id === el.nameId)?.name || '',
            statusId:
                el.participants?.find((entry) => entry.participantId === currentParticipant.id)
                    ?.statusId || ''
        }));
        setRows(formattedCourseProvisions);
    }, [currentCourseProvisions, externalCourseProviderDetails]);

    useEffect(() => {
        if (successMessage === 'Participant has been submitted to provision') {
            dispatch(searchCurrentCourseProvisions(activeCourseProvisionsSearchData));
        }
    }, [successMessage]);

    // HELPER FNS
    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <CurrentProvisionTableRow
                key={el.id}
                row={el}
                toggleDropdown={(id) => setOpenId(id)}
                openId={openId}
                isDisabled={isDisabled}
                returnState={returnState}
            />
        ));

    // RENDER
    return (
        <div className={app.container}>
            <h2 className={app.sectionHeading}>Current Provisions</h2>
            {currentCourseProvisionsMetaData?.last && rows?.length < 1 ? (
                <LoadingSpinner content="No current provisions found" />
            ) : (
                <ResultsTable
                    defaultOrderBy={'name'}
                    defaultRowsPerPage={rowMetaData.rowsPerPage}
                    apiParams={activeCourseProvisionsSearchData}
                    headCells={headCells}
                    loadingValues={loadingCurrentProvisions}
                    loadResults={searchCurrentCourseProvisions}
                    passRowMetaDataUp={setRowMetaData}
                    tableRows={createRows()}
                    totalResults={currentCourseProvisionsMetaData?.totalElements}
                    metaData={currentCourseProvisionsMetaData}
                />
            )}
        </div>
    );
};

CurrentProvisionManagement.propTypes = {
    isDisabled: PropTypes.bool,
    returnState: PropTypes.object
};

export default CurrentProvisionManagement;
